import {
  looksLikeSermonItem,
  sermonFileKind,
  sermonFileProblem,
  type SermonFileKind,
} from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { servicesKeys } from './useServices.ts'

/**
 * A plan's files: uploads and links on the plan or its series (a series' file
 * shows on every plan in it), and the PDFs generated from the plan — the
 * whole set, the service order, each song in this week's key.
 *
 * Keys live under the plan's and the services keys, so anything that
 * refreshes a plan (a song added, a key changed) refreshes its downloads too.
 */
export const planFilesKeys = {
  plan: servicesKeys.planFiles,
  downloads: servicesKeys.planDownloads,
  series: (seriesId: string) => [...servicesKeys.all(), 'series', seriesId, 'files'] as const,
  song: (songId: string) => [...servicesKeys.song(songId), 'files'] as const,
  arrangement: (arrangementId: string) =>
    [...servicesKeys.all(), 'arrangements', arrangementId, 'files'] as const,
  arrangementDownloads: (arrangementId: string) =>
    [...servicesKeys.all(), 'arrangements', arrangementId, 'downloads'] as const,
}

/**
 * Whose files: a plan's, one of its items' (the item's own attachments), a
 * series' (shown on each of its plans), a song's (every arrangement's), or
 * one arrangement's.
 */
export type FilesOwner =
  | { planId: string; planItemId: string }
  | { planId: string }
  | { seriesId: string }
  | { songId: string }
  | { arrangementId: string }

/** A plan's files read: its own, its items', its series'. */
function planFilesQuery(api: ReturnType<typeof useApi>, planId: string) {
  return {
    queryKey: planFilesKeys.plan(planId),
    enabled: planId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).files.get()
      if (error) throw error
      return data
    },
  }
}

export function usePlanFiles(planId: string) {
  return useQuery(planFilesQuery(useApi(), planId))
}

export type PlanFile = NonNullable<ReturnType<typeof usePlanFiles>['data']>[number]

/** One item's subsection of a plan's files: the item's title and its files. */
export interface ItemFileGroup {
  itemId: string
  title: string
  files: PlanFile[]
}

/**
 * A plan's files as its Files section shows them: the plan's own; then a
 * subsection per item that has files, named after the item, in the running
 * order; then the series'. An item with no files has no subsection.
 */
export function planFileGroups(
  files: readonly PlanFile[],
  itemOrder: readonly string[],
): { own: PlanFile[]; items: ItemFileGroup[]; series: PlanFile[] } {
  const byItem = new Map<string, ItemFileGroup>()
  for (const file of files) {
    if (file.owner !== 'item' || !file.planItemId) continue
    const group = byItem.get(file.planItemId) ?? {
      itemId: file.planItemId,
      title: file.itemTitle ?? 'Item',
      files: [],
    }
    group.files.push(file)
    byItem.set(file.planItemId, group)
  }
  const place = (id: string) => {
    const at = itemOrder.indexOf(id)
    return at === -1 ? itemOrder.length : at
  }
  return {
    own: files.filter((file) => file.owner === 'plan'),
    items: [...byItem.values()].sort((a, b) => place(a.itemId) - place(b.itemId)),
    series: files.filter((file) => file.owner === 'series'),
  }
}

/** A file the sermon can be read from: what kind it is, and why it can't be (too large). */
export interface SermonFileChoice {
  file: PlanFile
  kind: Exclude<SermonFileKind, 'unsupported'>
  problem: string | null
}

export interface SermonFileGroup {
  key: string
  /** The item's title, "This plan", or the series' name. */
  title: string
  owner: 'item' | 'plan' | 'series'
  files: SermonFileChoice[]
}

/**
 * The plan's files a sermon can be read from ("Use a file from this
 * plan"), grouped as its Files section groups them, with only the kinds a
 * sermon can be: an item that looks like the sermon ("Sermon", "Message")
 * first — its slides or manuscript are the likeliest — then the plan's own,
 * the series', and the other items' in running order. Empty groups go.
 */
export function sermonFileGroups(
  files: readonly PlanFile[],
  itemOrder: readonly string[],
): SermonFileGroup[] {
  const readable = files.filter(
    (file) =>
      file.kind === 'file' && sermonFileKind(file.contentType, file.fileName) !== 'unsupported',
  )
  const choice = (file: PlanFile): SermonFileChoice => ({
    file,
    kind: sermonFileKind(file.contentType, file.fileName) as SermonFileChoice['kind'],
    problem: sermonFileProblem(file),
  })
  const groups = planFileGroups(readable, itemOrder)
  const items: SermonFileGroup[] = groups.items.map((group) => ({
    key: `item:${group.itemId}`,
    title: group.title,
    owner: 'item',
    files: group.files.map(choice),
  }))
  return [
    ...items.filter((group) => looksLikeSermonItem(group.title)),
    { key: 'plan', title: 'This plan', owner: 'plan' as const, files: groups.own.map(choice) },
    {
      key: 'series',
      title: groups.series[0]?.seriesName ?? 'Series',
      owner: 'series' as const,
      files: groups.series.map(choice),
    },
    ...items.filter((group) => !looksLikeSermonItem(group.title)),
  ].filter((group) => group.files.length > 0)
}

/** One item's files and links, from its plan's list (one read for the whole plan). */
export function useItemFiles(planId: string, itemId: string) {
  return useQuery({
    ...planFilesQuery(useApi(), planId),
    select: (files: PlanFile[]) => files.filter((file) => file.planItemId === itemId),
  })
}

export function useSeriesFiles(seriesId: string) {
  const api = useApi()
  return useQuery({
    queryKey: planFilesKeys.series(seriesId),
    enabled: seriesId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.series({ id: seriesId }).files.get()
      if (error) throw error
      return data
    },
  })
}

/** A song's files and links: sheet music, rehearsal tracks, the YouTube video. */
export function useSongFiles(songId: string) {
  const api = useApi()
  return useQuery({
    queryKey: planFilesKeys.song(songId),
    enabled: songId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.songs({ id: songId }).files.get()
      if (error) throw error
      return data
    },
  })
}

/** One arrangement's own files and links (the song's come from `useSongFiles`). */
export function useArrangementFiles(arrangementId: string) {
  const api = useApi()
  return useQuery({
    queryKey: planFilesKeys.arrangement(arrangementId),
    enabled: arrangementId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services
        .arrangements({ id: arrangementId })
        .files.get()
      if (error) throw error
      return data
    },
  })
}

/** What an arrangement prints: its chart in each of its keys, and a lyric sheet. */
export function useArrangementDownloads(arrangementId: string) {
  const api = useApi()
  return useQuery({
    queryKey: planFilesKeys.arrangementDownloads(arrangementId),
    enabled: arrangementId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services
        .arrangements({ id: arrangementId })
        .downloads.get()
      if (error) throw error
      return data
    },
  })
}

export type ArrangementDownload = NonNullable<
  ReturnType<typeof useArrangementDownloads>['data']
>[number]

/** A generated arrangement PDF: its chart in a key, or its lyric sheet. */
export function arrangementDownloadUrl(
  baseUrl: string,
  arrangementId: string,
  download: ArrangementDownload,
): string {
  const root = `${baseUrl}/api/v1/services/arrangements/${arrangementId}/downloads`
  return download.kind === 'lyrics'
    ? `${root}/lyrics.pdf`
    : `${root}/chart.pdf?key=${encodeURIComponent(download.key ?? '')}`
}

/** What can be printed from the plan right now: the set, the order, each song. */
export function usePlanDownloads(planId: string) {
  const api = useApi()
  return useQuery({
    queryKey: planFilesKeys.downloads(planId),
    enabled: planId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).downloads.get()
      if (error) throw error
      return data
    },
  })
}

export type PlanDownload = NonNullable<ReturnType<typeof usePlanDownloads>['data']>[number]

/**
 * After any change, every list a file could appear in: this plan's, and —
 * for series files — every plan's (one series feeds many plans).
 */
function useFilesInvalidation() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: servicesKeys.all() })
}

/**
 * The whole upload as one mutation: ask for a slot, PUT the bytes straight to
 * storage, then confirm. The PUT goes with `fetch`, not the API client: it is
 * the bucket's origin with its own signed URL, and our session headers have
 * no business there.
 */
export function useUploadPlanFile(owner: FilesOwner) {
  const api = useApi()
  const invalidate = useFilesInvalidation()

  return useMutation({
    mutationFn: async (input: {
      title: string
      file: Blob
      fileName: string
      contentType: string
    }) => {
      const slotBody = {
        contentType: input.contentType,
        fileName: input.fileName,
        sizeBytes: input.file.size,
      }
      const slot =
        'planItemId' in owner
          ? await api.api.v1.services
              .plans({ id: owner.planId })
              .items({ itemId: owner.planItemId })
              .files['upload-url'].post(slotBody)
          : 'planId' in owner
            ? await api.api.v1.services
                .plans({ id: owner.planId })
                .files['upload-url'].post(slotBody)
            : 'seriesId' in owner
              ? await api.api.v1.services
                  .series({ id: owner.seriesId })
                  .files['upload-url'].post(slotBody)
              : 'songId' in owner
                ? await api.api.v1.services
                    .songs({ id: owner.songId })
                    .files['upload-url'].post(slotBody)
                : await api.api.v1.services
                    .arrangements({ id: owner.arrangementId })
                    .files['upload-url'].post(slotBody)
      if (slot.error) throw slot.error

      const uploaded = await fetch(slot.data.url, {
        method: 'PUT',
        headers: { 'content-type': input.contentType },
        body: input.file,
      })
      if (!uploaded.ok) throw new Error(`The file did not reach storage (${uploaded.status}).`)

      const confirm = { title: input.title, key: slot.data.key, fileName: input.fileName }
      const { data, error } =
        'planItemId' in owner
          ? await api.api.v1.services
              .plans({ id: owner.planId })
              .items({ itemId: owner.planItemId })
              .files.file.post(confirm)
          : 'planId' in owner
            ? await api.api.v1.services.plans({ id: owner.planId }).files.file.post(confirm)
            : 'seriesId' in owner
              ? await api.api.v1.services.series({ id: owner.seriesId }).files.file.post(confirm)
              : 'songId' in owner
                ? await api.api.v1.services.songs({ id: owner.songId }).files.file.post(confirm)
                : await api.api.v1.services
                    .arrangements({ id: owner.arrangementId })
                    .files.file.post(confirm)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

export function useAddPlanLink(owner: FilesOwner) {
  const api = useApi()
  const invalidate = useFilesInvalidation()
  return useMutation({
    /** No title: the link is named by its page's own title (fetched with its preview). */
    mutationFn: async (body: { title?: string | null; url: string }) => {
      const { data, error } =
        'planItemId' in owner
          ? await api.api.v1.services
              .plans({ id: owner.planId })
              .items({ itemId: owner.planItemId })
              .files.link.post(body)
          : 'planId' in owner
            ? await api.api.v1.services.plans({ id: owner.planId }).files.link.post(body)
            : 'seriesId' in owner
              ? await api.api.v1.services.series({ id: owner.seriesId }).files.link.post(body)
              : 'songId' in owner
                ? await api.api.v1.services.songs({ id: owner.songId }).files.link.post(body)
                : await api.api.v1.services
                    .arrangements({ id: owner.arrangementId })
                    .files.link.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

export function usePlanFileEditor() {
  const api = useApi()
  const invalidate = useFilesInvalidation()
  const rename = useMutation({
    mutationFn: async ({ fileId, title }: { fileId: string; title: string }) => {
      const { data, error } = await api.api.v1.services.files({ fileId }).patch({ title })
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
  const remove = useMutation({
    mutationFn: async (fileId: string) => {
      const { error } = await api.api.v1.services.files({ fileId }).delete()
      if (error) throw error
    },
    onSuccess: () => void invalidate(),
  })
  return { rename, remove }
}

/** An uploaded file's bytes, proxied by the API: the link needs a session. */
export const planFileUrl = (baseUrl: string, fileId: string) =>
  `${baseUrl}/api/v1/services/files/${fileId}/download`

/** A generated PDF, rendered from the plan as it is now. */
export function planDownloadUrl(baseUrl: string, planId: string, download: PlanDownload): string {
  const root = `${baseUrl}/api/v1/services/plans/${planId}/downloads`
  if (download.kind === 'set') return `${root}/set.pdf`
  if (download.kind === 'order') return `${root}/order.pdf`
  return `${root}/songs/${download.itemId ?? ''}`
}
